const EXPRESS_DOCK_HEIGHT = 60;

/**
 * Cart drawer — owns its open/close state (no Alpine), opens after
 * add-to-cart, and re-renders its wrapper from the section HTML that cart.js
 * bundles into every cart mutation ('cart:change' event).
 * @extends HTMLElement
 */
export class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.onCartChange = this.onCartChange.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onFooterSentinel = this.onFooterSentinel.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.addEventListener('click', this.onInnerClick);
  }

  connectedCallback() {
    document.addEventListener('cart:change', this.onCartChange);
    document.addEventListener('click', this.onDocumentClick);
    /* Keydown on the document, not the element: Escape has to work even in the
       moment before focus has landed inside the drawer.

       Capture phase, because the innermost-first Escape below has to read the
       quantity menus before cart.js hides them. cart.js registers its own
       document keydown listener and loads from <head> (theme.liquid) while this
       module loads with the drawer markup, so in the bubble phase cart.js
       always runs first and this listener would only ever see menus that were
       already closed. */
    document.addEventListener('keydown', this.onKeydown, true);
    document.addEventListener('focusin', this.onFocusIn);
    this.observeFooter();
    this.observeExpress();
    this.wasOpen = this.classList.contains('cart-open');
    this.syncOpenState();
    this.observeOpenState();
  }

  /* The drawer is never removed from the DOM and is not display:none when
     closed - the panel is translated off-canvas - so its controls stayed
     focusable, and with role="dialog" on the host it read as a permanently
     open dialog. `inert` takes it out of the accessibility tree and the tab
     order together; browsers without support behave exactly as before.

     aria-modal rides the same state. It is only honest while the drawer is
     open and focus is trapped inside it, so it is set and removed here rather
     than authored into the markup. */
  syncOpenState() {
    const open = this.classList.contains('cart-open');
    this.toggleAttribute('inert', !open);
    if (open) this.setAttribute('aria-modal', 'true');
    else this.removeAttribute('aria-modal');

    if (open === this.wasOpen) return;
    this.wasOpen = open;
    if (open) this.captureFocus();
    else this.restoreFocus();
  }

  /* open()/close() are not the only way the drawer opens: cart.js and
     theme.js both add and remove `cart-open` on the element directly. Watching
     the class is what keeps the inert state correct on those paths too -
     syncing only inside the methods would leave the drawer visible but inert,
     which is worse than not hiding it at all. Focus handling hangs off the same
     observer for the same reason, and syncOpenState only acts on a genuine
     transition, so the direct call in open()/close() and the observer firing
     afterwards do not move focus twice. */
  observeOpenState() {
    this.openStateObserver = new MutationObserver(() => this.syncOpenState());
    this.openStateObserver.observe(this, { attributes: true, attributeFilter: ['class'] });
  }

  disconnectedCallback() {
    document.removeEventListener('cart:change', this.onCartChange);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown, true);
    document.removeEventListener('focusin', this.onFocusIn);
    this.footerObserver?.disconnect();
    this.expressObserver?.disconnect();
    this.openStateObserver?.disconnect();
  }

  observeFooter() {
    this.footerObserver?.disconnect();

    const root = this.querySelector('.cart-items-wrapper');
    const sentinel = this.querySelector('.cart-footer-sentinel');
    if (!root || !sentinel) return;

    this.footerObserver = new IntersectionObserver(this.onFooterSentinel, {
      root,
      threshold: 0,
    });
    this.footerObserver.observe(sentinel);
  }

  onFooterSentinel(entries) {
    const entry = entries[entries.length - 1];
    const footer = this.querySelector('.cart-footer');
    footer?.classList.toggle('cart-footer--pinned', !entry.isIntersecting);
  }

  observeExpress() {
    this.expressObserver?.disconnect();
    this.measureExpress();

    const strip = this.querySelector('.cart-drawer__express');
    if (!strip) return;
    this.expressObserver = new ResizeObserver(() => this.measureExpress());
    this.expressObserver.observe(strip);
  }

  measureExpress() {
    const strip = this.querySelector('.cart-drawer__express');
    const height = strip?.getBoundingClientRect().height;
    if (!height) return this.undockExpress();

    const hidden = Math.max(Math.round(height - EXPRESS_DOCK_HEIGHT), 0);
    this.style.setProperty('--cart-express-hidden', `${hidden}px`);
    this.classList.add('express-docked');
  }

  undockExpress() {
    this.style.removeProperty('--cart-express-hidden');
    this.classList.remove('express-docked');
  }

  get sectionId() {
    return this.closest('.shopify-section')?.id.replace('shopify-section-', '');
  }

  open() {
    if (this.classList.contains('cart-open')) return;
    if (
      document.querySelector('.theme-modal.reveal') &&
      typeof window.closeThemeModal === 'function'
    ) {
      window.closeThemeModal();
    }
    this.classList.add('cart-open');
    this.syncOpenState();
    document.body.style.overflow = 'hidden';
    this.measureExpress();
  }

  close() {
    this.classList.remove('cart-open');
    this.syncOpenState();
    /* A theme modal may still own the page (it locks with the body.modal-active
       class, not this inline style). Only hand scrolling back when nothing else
       holds it — both checks are needed to cover the 300ms unreveal window
       where .reveal is already gone but the class is still set. */
    if (
      !document.querySelector('.theme-modal.reveal') &&
      !document.body.classList.contains('modal-active')
    ) {
      document.body.style.overflow = '';
    }
  }

  toggle() {
    if (this.classList.contains('cart-open')) {
      this.close();
    } else {
      this.open();
    }
  }

  onDocumentClick(event) {
    const bubble = event.target.closest('.header-cart-bubble');
    if (bubble) {
      event.preventDefault();
      this.toggle();
    }
  }

  /* Everything focusable the drawer currently holds, in document order. Read
     fresh on each Tab: render() replaces the whole wrapper, so a list cached on
     open goes stale the moment a line item changes.

     This is the whole set of elements that are focusable without a tabindex,
     not just the form controls, because Shopify renders the accelerated
     checkout buttons into .cart-drawer__express as iframes
     (content_for_additional_checkout_buttons). Leaving embedded content out
     made the ordinary checkout button look like the last stop whenever the
     footnote and upsell settings are both off, and Tab off it wrapped to the
     top of the drawer instead of reaching Shop Pay or PayPal at all. */
  focusableChildren() {
    const selector = [
      'a[href]',
      'area[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'iframe',
      'object',
      'embed',
      'audio[controls]',
      'video[controls]',
      'summary',
      '[contenteditable]:not([contenteditable="false"])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    return Array.from(this.querySelectorAll(selector)).filter(
      (el) => !el.closest('[inert]') && el.getClientRects().length > 0
    );
  }

  /* Remember where focus came from, then put it on the dialog itself rather
     than its first control: a screen reader announces the drawer's name and
     role, and Tab carries on into the contents from there. */
  captureFocus() {
    const active = document.activeElement;
    this.returnFocusTo =
      active && active !== document.body && !this.contains(active) ? active : null;
    this.focus({ preventScroll: true });
  }

  /* syncOpenState sets `inert` before calling this, which has already pushed
     focus out to <body> - so this is a restore, never a steal. The cart bubble
     is the fallback when the original trigger has been re-rendered away; the
     header renders one for desktop and one for mobile, so take whichever is
     actually on screen rather than the first in the DOM. */
  restoreFocus() {
    const previous = this.returnFocusTo;
    this.returnFocusTo = null;
    const bubble = Array.from(document.querySelectorAll('.header-cart-bubble')).find(
      (el) => el.getClientRects().length > 0
    );
    const target = previous && previous.isConnected ? previous : bubble;
    target?.focus?.({ preventScroll: true });
  }

  /* The drawer only owns Tab and Escape while focus is its own - or nobody's.
     Layers above it live outside its subtree: cart-gift-swap appends itself to
     <body> rather than into the drawer (component-cart-drawer.js replaces
     .drawer__wrapper on every cart:change and would destroy it), and runs its
     own Escape and focus trap over both itself and its nested size chart.
     Without this the trap would drag focus out of the open picker and back
     into the drawer behind it, and one Escape would close both layers at once.

     Focus on <body> counts as the drawer's: nothing else is holding it, so
     Escape should still shut the drawer and Tab should still pull focus in. */
  ownsKeys() {
    const active = document.activeElement;
    return !active || active === document.body || this.contains(active);
  }

  /* The Tab handler cannot contain focus on its own once the express-checkout
     iframes are in play. Key events do not cross a document boundary, so while
     focus sits inside a wallet frame the drawer never sees the keypress, and
     the browser walks straight out of the drawer when that frame runs out of
     controls. Watching focusin catches the landing instead of the keypress.

     Only cart-gift-swap is exempt, because it is the only layer that actually
     sits above the drawer: z-index 999999999999 (cart-gift-swap.css) against
     the drawer's 99999999999 (cart.css). Exempting dialogs in general let
     things that render *behind* the drawer take focus off it - the newsletter
     popup is role="dialog" at z-index 997 and focuses its own close button on a
     500ms timer (ccPopup.open in theme.js), and .theme-modal sits at 20. Focus
     landing in either of those while the drawer is open is a bug to correct,
     not an intent to honour. */
  onFocusIn(event) {
    if (!this.classList.contains('cart-open')) return;
    const target = event.target;
    if (this.contains(target)) return;
    if (target.closest?.('cart-gift-swap')) return;
    const items = this.focusableChildren();
    (items[0] || this).focus({ preventScroll: true });
  }

  onKeydown(event) {
    if (!this.classList.contains('cart-open')) return;
    if (!this.ownsKeys()) return;

    if (event.key === 'Escape') {
      /* Escape closes the innermost thing first. cart.js has its own Escape
         listener that shuts the line-item quantity menus; when one is open, let
         that handle the key rather than dismissing the whole drawer under it. */
      if (this.querySelector('.cart-quantity__menu:not([hidden])')) return;
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    const items = this.focusableChildren();
    if (!items.length) {
      /* An empty cart still renders a link, but guard anyway: without this the
         trap would let Tab walk out into the page behind the drawer. */
      event.preventDefault();
      this.focus({ preventScroll: true });
      return;
    }

    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    const inside = this.contains(active);

    if (event.shiftKey && (active === this || active === first || !inside)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !inside)) {
      /* Forward from the host itself needs no help - the browser tabs into the
         children on its own. */
      event.preventDefault();
      first.focus();
    }
  }

  onInnerClick(event) {
    if (event.target.closest('.drawer-overlay, .cart-drawer__close')) {
      this.close();
    }
  }

  onCartChange(event) {
    const html = event.detail.sections?.[this.sectionId];
    if (html) {
      this.render(html, event.detail.action);
    } else if (event.detail.action === 'change' || event.detail.action === 'add') {
      /* No section HTML came back (e.g. a mutation made outside cart.js).
         Re-sync rather than silently going stale. The action allow-list keeps
         this off 'refresh' (Cart.refresh() re-dispatches cart:change, which
         would recurse) and off 'update' (the cart note posts with
         withSections: false and legitimately returns none). */
      console.warn('[CartDrawer] No section HTML for', this.sectionId, '— refreshing.');
      window.Cart?.refresh();
    }
    if (event.detail.action === 'add' && event.detail.source !== 'cart-tier-rewards') {
      this.open();
    }
  }

  render(html, action) {
    const next = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('#cart-drawer .drawer__wrapper');
    const current = this.querySelector('.drawer__wrapper');
    if (!current) return; // drawer markup gone; nothing to patch
    if (!next) {
      /* Warn only — recovery lives in onCartChange, so refreshing here could
         loop against it. */
      console.warn('[CartDrawer] Section HTML had no .drawer__wrapper — leaving DOM intact.');
      return;
    }

    const scrollTop = current.querySelector('.cart-items-wrapper')?.scrollTop || 0;
    /* innerHTML drops whatever held focus, sending it to <body> and out of the
       dialog. Put it back on the drawer so a keyboard user is not silently
       returned to the page behind an open drawer. */
    const hadFocus = this.contains(document.activeElement);
    current.innerHTML = next.innerHTML;
    if (hadFocus && this.classList.contains('cart-open')) this.focus({ preventScroll: true });

    const itemsWrapper = current.querySelector('.cart-items-wrapper');
    if (itemsWrapper) itemsWrapper.scrollTop = action === 'add' ? 0 : scrollTop;

    this.observeFooter();
    this.observeExpress();
    document.dispatchEvent(new CustomEvent('cart-drawer:rendered', { detail: { action } }));
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
